import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type DragEvent as ReactDragEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react'
import {useTranslation} from 'react-i18next'
import {generateFilmstripThumbnails} from '../../../common/utils/videoThumbnails'
import {useConverterVideoUpload} from './useConverterVideoUpload'
import {MAX_TRIM_DURATION_SEC} from '../../../common/constants/videoToGifLimits'
import type {LoadedVideo} from './loadConverterVideoFile'

interface VideoTrimFilmstripProps {
  video: LoadedVideo | null
  trimStart: number
  trimEnd: number
  onChange: (start: number, end: number) => void
  onVideoLoaded: (video: LoadedVideo) => void
}

const THUMBNAIL_COUNT = 16
const MIN_WINDOW_SEC = 0.5

type DragMode = 'start' | 'end' | 'move'

interface DragState {
  mode: DragMode
  startX: number
  trackW: number
  trimStartAtDragBegin: number
  trimEndAtDragBegin: number
}

const clamp = (value: number, min: number, max: number): number => Math.min(max, Math.max(min, value))

const formatTime = (seconds: number): string => {
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return `${m}:${String(s).padStart(2, '0')}`
}

/** Edge handles ('start'/'end') CLAMP against each other — dragging one past
 * the other simply stops at MIN_WINDOW_SEC apart, it never pushes/pulls the
 * opposite handle. Moving the whole trim window is a separate 'move' mode
 * (drag the selection body) that shifts both edges together by the same
 * delta, clamped to stay inside [0, duration]. */
const computeDrag = (
  mode: DragMode,
  dxSec: number,
  startAtBegin: number,
  endAtBegin: number,
  duration: number,
): {start: number; end: number} => {
  if (mode === 'move') {
    const width = endAtBegin - startAtBegin
    const newStart = clamp(startAtBegin + dxSec, 0, duration - width)
    return {start: newStart, end: newStart + width}
  }

  if (mode === 'start') {
    const minStart = Math.max(0, endAtBegin - MAX_TRIM_DURATION_SEC)
    const maxStart = endAtBegin - MIN_WINDOW_SEC
    const newStart = clamp(startAtBegin + dxSec, minStart, maxStart)
    return {start: newStart, end: endAtBegin}
  }

  const minEnd = startAtBegin + MIN_WINDOW_SEC
  const maxEnd = Math.min(duration, startAtBegin + MAX_TRIM_DURATION_SEC)
  const newEnd = clamp(endAtBegin + dxSec, minEnd, maxEnd)
  return {start: startAtBegin, end: newEnd}
}

/** iPhone-Photos-style trim: a thumbnail filmstrip with two draggable edge
 * handles (clamp-only, see computeDrag) plus a drag-the-middle 'move' mode
 * on the selection body, and a live video preview that seeks to whichever
 * handle is being dragged. Also the drop target for replacing the source
 * video (mirrors ImagePreviewCanvas). */
const VideoTrimFilmstrip = ({video, trimStart, trimEnd, onChange, onVideoLoaded}: VideoTrimFilmstripProps) => {
  const {t} = useTranslation()
  const videoRef = useRef<HTMLVideoElement>(null)
  const trackRef = useRef<HTMLDivElement>(null)
  const dragRef = useRef<DragState | null>(null)
  const seekInFlightRef = useRef(false)
  const pendingSeekRef = useRef<number | null>(null)
  const [thumbnails, setThumbnails] = useState<string[]>([])
  const [thumbnailsLoading, setThumbnailsLoading] = useState(false)
  const [activeHandle, setActiveHandle] = useState<DragMode | null>(null)
  const [dragOver, setDragOver] = useState(false)
  const {busy: uploadBusy, error: uploadError, handleFileSelected} = useConverterVideoUpload(onVideoLoaded)

  // Regenerate the filmstrip whenever a (new) video is loaded. Deliberately does
  // NOT depend on trimStart/trimEnd — dragging the handles must never retrigger
  // this (it would re-run all 16 seeks on every pointermove).
  useEffect(() => {
    const el = videoRef.current
    if (!el || !video) return
    let cancelled = false

    const generate = async () => {
      setThumbnails([])
      setThumbnailsLoading(true)
      if (el.readyState < 1) {
        await new Promise<void>((resolve) => {
          const onLoaded = () => {
            el.removeEventListener('loadedmetadata', onLoaded)
            resolve()
          }
          el.addEventListener('loadedmetadata', onLoaded)
        })
      }
      if (cancelled) return
      const shots = await generateFilmstripThumbnails(el, THUMBNAIL_COUNT)
      if (!cancelled) {
        setThumbnails(shots)
        setThumbnailsLoading(false)
      }
    }
    void generate()

    return () => {
      cancelled = true
    }
  }, [video])

  // Flushes queued seeks one at a time: a non-keyframe-aligned seek can take
  // longer to decode than pointermove events arrive during a fast drag, so
  // firing `el.currentTime = x` on every state change let later assignments
  // preempt earlier ones before they ever painted a frame — visually the
  // preview looked frozen until the drag stopped. Queuing the LATEST desired
  // time and only advancing once the previous seek's 'seeked' event fires
  // keeps the preview updating continuously at whatever rate the browser can
  // actually decode, instead of skipping straight to the final position.
  useEffect(() => {
    const el = videoRef.current
    if (!el || !video) return
    seekInFlightRef.current = false
    pendingSeekRef.current = null
    const handleSeeked = () => {
      if (pendingSeekRef.current !== null) {
        const next = pendingSeekRef.current
        pendingSeekRef.current = null
        el.currentTime = next
      } else {
        seekInFlightRef.current = false
      }
    }
    el.addEventListener('seeked', handleSeeked)
    return () => el.removeEventListener('seeked', handleSeeked)
  }, [video])

  // Live preview: seek to whichever handle is active while dragging, or to the
  // start position at rest — reacts to trim changes even outside a drag (e.g.
  // programmatic resets), not just live dragging.
  useEffect(() => {
    const el = videoRef.current
    if (!el || !video || thumbnailsLoading) return
    const target = activeHandle === 'end' ? trimEnd : trimStart
    if (seekInFlightRef.current) {
      pendingSeekRef.current = target
    } else {
      seekInFlightRef.current = true
      el.currentTime = target
    }
  }, [activeHandle, trimStart, trimEnd, thumbnailsLoading, video])

  const beginDrag = useCallback(
    (mode: DragMode) => (e: ReactPointerEvent<HTMLDivElement>) => {
      e.preventDefault()
      e.stopPropagation()
      const track = trackRef.current
      if (!track) return
      const box = track.getBoundingClientRect()
      dragRef.current = {
        mode,
        startX: e.clientX,
        trackW: box.width,
        trimStartAtDragBegin: trimStart,
        trimEndAtDragBegin: trimEnd,
      }
      setActiveHandle(mode)
      e.currentTarget.setPointerCapture(e.pointerId)
    },
    [trimStart, trimEnd],
  )

  const handlePointerMove = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      const drag = dragRef.current
      if (!drag || !video) return
      const dxFrac = drag.trackW > 0 ? (e.clientX - drag.startX) / drag.trackW : 0
      const dxSec = dxFrac * video.duration
      const result = computeDrag(drag.mode, dxSec, drag.trimStartAtDragBegin, drag.trimEndAtDragBegin, video.duration)
      onChange(result.start, result.end)
    },
    [video, onChange],
  )

  const handlePointerUp = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    dragRef.current = null
    setActiveHandle(null)
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId)
  }, [])

  const handleDrop = useCallback(
    (e: ReactDragEvent<HTMLDivElement>) => {
      e.preventDefault()
      setDragOver(false)
      const file = e.dataTransfer.files?.[0]
      if (file) void handleFileSelected(file)
    },
    [handleFileSelected],
  )
  const handleDragOver = useCallback((e: ReactDragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setDragOver(true)
  }, [])
  const handleDragLeave = useCallback(() => setDragOver(false), [])

  if (!video) {
    return (
      <div
        className={dragOver ? 'vtg-filmstrip-drop is-dragover' : 'vtg-filmstrip-drop'}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        <p className="cvt-hint">{t('videoToGif.trim.needVideo')}</p>
        {uploadError && <p className="cvt-error">{uploadError}</p>}
      </div>
    )
  }

  const duration = video.duration > 0 ? video.duration : 1
  const startFrac = clamp(trimStart / duration, 0, 1)
  const endFrac = clamp(trimEnd / duration, 0, 1)

  return (
    <div
      className={dragOver ? 'vtg-filmstrip-wrap is-dragover' : 'vtg-filmstrip-wrap'}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
    >
      <video ref={videoRef} src={video.objectUrl} className="vtg-filmstrip-preview" muted playsInline>
        <track kind="captions" />
      </video>

      <div className="vtg-filmstrip-track" ref={trackRef}>
        {thumbnailsLoading ? (
          <div className="vtg-filmstrip-loading">{t('videoToGif.trim.generatingPreview')}</div>
        ) : (
          <div className="vtg-filmstrip-thumbs">
            {thumbnails.map((src, i) => (
              <img key={i} src={src} alt="" className="vtg-filmstrip-thumb" draggable={false} />
            ))}
          </div>
        )}

        <div
          className="vtg-filmstrip-selection"
          style={{left: `${startFrac * 100}%`, width: `${(endFrac - startFrac) * 100}%`}}
          onPointerDown={beginDrag('move')}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
        >
          <div className="vtg-filmstrip-handle vtg-filmstrip-handle-start" onPointerDown={beginDrag('start')} />
          <div className="vtg-filmstrip-handle vtg-filmstrip-handle-end" onPointerDown={beginDrag('end')} />
        </div>
      </div>

      <div className="vtg-filmstrip-times">
        <span>{formatTime(trimStart)}</span>
        <span className="vtg-filmstrip-times-total">{formatTime(video.duration)}</span>
        <span>{formatTime(trimEnd)}</span>
      </div>

      {uploadBusy && <p className="cvt-hint">{t('videoToGif.upload.busy')}</p>}
      {uploadError && <p className="cvt-error">{uploadError}</p>}
    </div>
  )
}

export default VideoTrimFilmstrip
